import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { BrowserRouter, useHistory, useLocation } from 'react-router-dom';
import { mapValues, pick } from 'lodash';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import qs from 'qs';
import { CardsFilter } from '../cards';
import { useExperienceFilter } from './api';

const ExperienceFilterKernel = ({
  initialCategory,
  filters,
  displayAllEntries = true,
}) => {
  const location = useLocation();
  const history = useHistory();

  const getQueryObjectFromUrl = () => {
    const { search } = location;
    const searchParams = new URLSearchParams(search);
    const categoryGroupTitles = filters.map(
      (filter) => filter.categoryGroupTitle
    );

    // Only get valid category group titles
    return pick(Object.fromEntries(searchParams), categoryGroupTitles);
  };

  const [filterOptions, setFilterOptions] = useState(getQueryObjectFromUrl());

  const [
    fetchedExperiences,
    status,
    haveMoreExperiences,
    setCategoryIds,
    setFetchAll,
  ] = useExperienceFilter(displayAllEntries);

  const showDisplayAllButton = !displayAllEntries && haveMoreExperiences;

  useEffect(() => {
    const query = getQueryObjectFromUrl();
    setFilterOptions(query);
    const categoryIds = Object.values(query);
    if (initialCategory) {
      // An ID to be used to always filter the entries by regardless of what is set through the UI.
      categoryIds.push(initialCategory);
    }
    setCategoryIds(categoryIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleSelectChange = (categoryGroupTitle, categoryID) => {
    const query = getQueryObjectFromUrl();
    const newQueryObject = {
      ...query,
      [categoryGroupTitle]: categoryID,
    };
    const searchParamsString = qs.stringify(newQueryObject);
    history.push({
      pathname: location.pathname,
      search: searchParamsString,
    });
  };

  const handleResetFilterButtonClick = () => {
    const query = getQueryObjectFromUrl();
    const queryForAllObject = mapValues(query, () => '0');
    const searchParamsString = qs.stringify(queryForAllObject);
    history.push({
      pathname: location.pathname,
      search: searchParamsString,
    });
  };

  const emptyState = (
    <div className="d-flex flex-column align-items-center gap-3 mt-5">
      <p className="fw-bold text-x-large">Sorry, no products found</p>
      <p>
        Try adjusting your {pluralize('filters', filters.length)} to find other
        products
      </p>
      <Button variant="outline-primary" onClick={handleResetFilterButtonClick}>
        Reset {pluralize('filters', filters.length)}
      </Button>
    </div>
  );

  const filtersArray = filters.map((filter) => ({
    title: filter.categoryGroupTitle,
    options: [
      { value: 0, label: 'Viewing All' },
      ...filter.filters.map((category) => ({
        value: category.categoryID,
        label: category.categoryLabel,
      })),
    ],
    onChange: ({ value, title }) => handleSelectChange(title, value),
    initialValue: filterOptions[filter.categoryGroupTitle] || '0',
  }));

  const moreLink = showDisplayAllButton ? (
    <div className="d-flex justify-content-center mt-5">
      <Button variant="outline-primary" onClick={() => setFetchAll(true)}>
        View all products
      </Button>
    </div>
  ) : null;

  return (
    <CardsFilter
      filters={filtersArray}
      cards={fetchedExperiences}
      fetchStatus={status}
      emptyState={emptyState}
      moreLink={moreLink}
    />
  );
};

const ExperienceFilter = (props) => (
  <BrowserRouter>
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <ExperienceFilterKernel {...props} />
  </BrowserRouter>
);

export { ExperienceFilter, ExperienceFilterKernel };

ExperienceFilterKernel.propTypes = {
  initialCategory: PropTypes.number,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      categoryGroupTitle: PropTypes.string,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          categoryID: PropTypes.number,
          categoryLabel: PropTypes.string,
        })
      ),
    })
  ),
  displayAllEntries: PropTypes.bool,
};
